import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import YesNoQuestion from '../Question/YesNoQuestion';
import CheckboxQuestion from '../Question/CheckboxQuestion';
import DropdownQuestion from '../Question/DropdownQuestion';
import ChoiceQuestion from '../Question/ChoiceQuestion';
import Image from '../Image';

import styles from './styles.module.scss';

const Questions = ({ stage, step, back, backStage, setStepAnswers, recommendation }) => {
  const { t } = useTranslation();

  const [questions, setAnswers] = useState(step.questions);
  const [activeQuestion, setActiveQuestion] = useState(1);
  const [canSubmit, setCanSubmit] = useState(() => {
    const unansweredQuestions = questions.filter((question) => question.answer === null || question.answer.length === 0);
    return 0 === unansweredQuestions.length;
  });
  const isDesktop = true;

  const setSelectedAnswer = (question, answer) => {
    const answers = questions.reduce((res, q) => {
      const answers = q.answer;

      if (q.multiple) {
        const answerExists = answers.indexOf(answer);

        // Remove if already exists
        if (-1 !== answerExists) {
          answers.splice(answerExists, 1);
        } else {
          answers.push(answer);
        }
      }

      const idx = res.indexOf(q);
      res[idx] = q.key === question.key ? { ...q, answer: question.multiple ? answers : answer } : { ...q };

      return res;
    }, questions);

    checkIfCanSubmit();

    setAnswers([...answers]);

    // Adobe tracking (question + answer)
    if (window.globalLmd) {
      window.lmd = window.globalLmd;
      window.lmd.custom = t(question.name) + ': ' + t(answer.text);
      window._satellite.track('CustomInteraction');
    }

    if (isDesktop) {
      const curQuestion = question.key.replace('q', '');

      if ((parseInt(curQuestion)) < questions.length && parseInt(curQuestion) === activeQuestion) {
        setActiveQuestion(activeQuestion + 1);
      }
    }
  };

  const checkUnansweredQuestions = useCallback(() => {
    return questions.filter((question) => question.answer === null || question.answer.length === 0);
  }, [questions]);

  const checkIfCanSubmit = useCallback(() => {
    const unansweredQuestions = checkUnansweredQuestions();
    setCanSubmit(0 === unansweredQuestions.length);
  }, [checkUnansweredQuestions, setCanSubmit]);

  const nextStep = () => {
    if (canSubmit) {
      // Scroll to top if scrolled down
      if (window.scrollY) {
        window.scrollTo(0, 0);
      }

      setStepAnswers(questions, (step) => {
        setAnswers(step.questions);
        setCanSubmit(false);
      });
    }
  };

  const previousStep = () => {
    back((step) => {
      setAnswers(step.questions);
    });
  }

  const previousStage = () => {
    backStage((step) => {
      setAnswers(step.questions);
    });
  }

  useEffect(() => {
    checkIfCanSubmit();
  }, [questions, checkIfCanSubmit]);

  return <div className={styles.questions}>
    <h2>
      <span className={styles.section_title}>
        {step.key.replace('step_', '')} {t(`tool_ui.global.step_of`)} {stage.steps.length} {t(`tool_ui.global.step_plural`)}
      </span>
      {t(`tool_ui.${stage.key}.title`)}
    </h2>
    <h3>
      <Image src={`images_iax/icon-${stage.key}_${step.key}`} className={styles.icon} />
      {t(`tool_ui.${stage.key}.${step.key}_name`)}
    </h3>
    <div className={styles.questions_holder}>
      {questions.map((question, index) => <div key={index} className={styles.question} style={{zIndex: questions.length - index}}>
        {
          'checkboxes' === question.type ?
            <CheckboxQuestion
              number={index+1}
              total={questions.length}
              stage={stage}
              step={step}
              activeQuestion={activeQuestion}
              question={question}
              setSelectedAnswer={setSelectedAnswer}
              disabled={index > 0 ? null === questions[index - 1].answer : false}
            />
          :
          'choice' === question.type ?
            <ChoiceQuestion
              number={index+1}
              total={questions.length}
              stage={stage}
              step={step}
              activeQuestion={activeQuestion}
              question={question}
              setSelectedAnswer={setSelectedAnswer}
              disabled={index > 0 ? null === questions[index - 1].answer : false}
            />
          :
          'dropdown' === question.type ?
            <DropdownQuestion
              number={index+1}
              total={questions.length}
              stage={stage}
              step={step}
              activeQuestion={activeQuestion}
              question={question}
              setSelectedAnswer={setSelectedAnswer}
              disabled={index > 0 ? null === questions[index - 1].answer : false}
            />
          :
            <YesNoQuestion
              number={index+1}
              total={questions.length}
              stage={stage}
              step={step}
              activeQuestion={activeQuestion}
              question={question}
              setSelectedAnswer={setSelectedAnswer}
              disabled={index > 0 ? null === questions[index - 1].answer : false}
            />
        }
      </div>)}
    </div>
    <div className={styles.buttons + ' ' + ('step_1' !== step.key ? styles.has_back : 'step_1' === step.key && 'devices' === stage.key && 'both' === recommendation ? styles.has_back : '')}>
      {'step_1' !== step.key ? <button onClick={previousStep} className={styles.back}>
        {t('tool_ui.global.previous')}
      </button> : ''}
      {'step_1' === step.key && 'devices' === stage.key && 'both' === recommendation ? <button onClick={previousStage} className={styles.back}>
        {t('tool_ui.global.previous')}
      </button> : ''}
      <button
        onClick={nextStep}
        disabled={!canSubmit}
      >
        {'devices' === stage.key && stage.curStep > stage.steps.length ? t('buttons.get_results') : t('tool_ui.global.next')}
      </button>
    </div>
  </div>;
}

export default Questions;
